import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';
import SpinnerLoading from './SpinnerLoading';
import { usePlayerStore } from '../../stores/player-store';
import { useUserStore } from '../../stores/user-store';
import './YouTubeIframe.scss';

// Port of src/app/shared/components/you-tube-iframe/ (mypart-you-tube-iframe).
//
// Angular contract → React:
// - @Input youtube_id / timestamp → props
// - @Output playSongClicked → onPlaySongClicked callback prop
// - host [ngClass] from call sites (e.g. 'p-4' when highlighted) → className
//   prop rendered on a host-equivalent wrapper div
// - ResultItem called the component's public playVideo() via @ViewChild →
//   exposed through an imperative handle (React 19 ref-as-prop).
// - effect #1: when another iframe starts playing (playingIframeId !== ours),
//   hide our iframe. effect #2: when any mp3 audio starts (playingAudioUrl),
//   hide our iframe. Both replicated as useEffects on the player store.
// - iframeSrc was rebuilt in ngOnChanges only when youtube_id changed (the
//   timestamp current value was read at that moment) → useMemo on both.
// - iframeLoaded was a plain field that was never reset, so the spinner only
//   shows for the first load of the iframe — preserved.
// - data-play-target marks the clickable inline container; Player.tsx uses it
//   for next/previous navigation (the React equivalent of Angular's
//   firstChild/lastChild DOM traversal into this component).
//
// NOTE(port): we inject an EMPTY placeholder <div id={youtube_id}> imperatively
// into a React-owned host, NOT a ready-made <iframe>. Player.startVideoInit()
// hands that placeholder to `new YT.Player(div, { videoId, playerVars })`, which
// CREATES its own <iframe> inside it (and destroy() removes it). Two reasons it
// must be the API that creates the iframe, not us:
//   1. Reliability. If we inject a fully-formed <iframe src=…/embed/…> it begins
//      loading YouTube immediately; by the time YT.Player() binds to it (~300ms
//      later) the embed may have ALREADY finished loading, so the API misses the
//      iframe's `load` event, its postMessage handshake desyncs, and `onReady`
//      NEVER fires — leaving showControls=false (locked mini-player, blocked
//      cursor) and playVideo() never called (no autoplay; you had to click
//      YouTube's own play button). Letting the API create the iframe lets it own
//      the load lifecycle, so onReady fires every time and playerVars.autoplay
//      actually applies. (Binding to a pre-existing iframe also makes the API
//      IGNORE videoId/playerVars entirely.)
//   2. No crash. If React owned the <iframe>, the next showIframe→false render
//      would call removeChild on a node YouTube had already swapped out —
//      "Failed to execute 'removeChild' on 'Node'" — crashing the result-item
//      error boundary and wedging the bottom player. Browser-owning the node
//      (the same approach the SearchBar uses for its chips) keeps it outside
//      React's reconciliation: React never removes it, and tearing it down with
//      replaceChildren() is safe whether the live node is YouTube's, ours, or
//      already gone.

export interface YouTubeIframeHandle {
  playVideo: () => void;
}

export interface YouTubeIframeProps {
  youtube_id?: string;
  timestamp: number;
  onPlaySongClicked: () => void;
  /** Classes the Angular call sites put on the <mypart-you-tube-iframe> host. */
  className?: string;
  ref?: Ref<YouTubeIframeHandle>;
}

export default function YouTubeIframe({
  youtube_id,
  onPlaySongClicked,
  className,
  ref
}: YouTubeIframeProps) {
  const [showIframe, setShowIframe] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // React-owned host the imperative placeholder/iframe lives inside (always empty
  // from React's perspective — see the NOTE(port) above).
  const iframeHostRef = useRef<HTMLDivElement>(null);
  // Watches the host so the spinner clears once the YouTube API inserts its iframe.
  const observerRef = useRef<MutationObserver | null>(null);

  const playingIframeId = usePlayerStore(s => s.playingIframeId);
  const playingAudioUrl = usePlayerStore(s => s.playingAudioUrl);

  useEffect(() => {
    if (playingIframeId !== youtube_id) {
      setShowIframe(false);
    }
  }, [playingIframeId, youtube_id]);

  useEffect(() => {
    if (playingAudioUrl) {
      setShowIframe(false);
    }
  }, [playingAudioUrl]);

  // Inject an EMPTY placeholder <div id={youtube_id}> when shown; clear the host
  // when hidden. Player.startVideoInit() hands this placeholder to new YT.Player(),
  // which creates its own iframe inside it (see NOTE(port) above). Clearing with
  // replaceChildren() never throws, even if the API already swapped our node for
  // its iframe or destroy() removed it entirely.
  useEffect(() => {
    const host = iframeHostRef.current;
    if (!host) {
      return;
    }
    if (showIframe && youtube_id) {
      if (!host.firstChild) {
        const placeholder = document.createElement('div');
        placeholder.id = youtube_id;
        placeholder.className = 'w-full h-full';
        host.appendChild(placeholder);
        setIframeLoaded(false);
        // Reveal (hide the spinner) once the API has inserted its <iframe>.
        observerRef.current?.disconnect();
        const observer = new MutationObserver(() => {
          if (host.querySelector('iframe')) {
            setIframeLoaded(true);
            observer.disconnect();
          }
        });
        observer.observe(host, { childList: true, subtree: true });
        observerRef.current = observer;
      }
    } else {
      observerRef.current?.disconnect();
      observerRef.current = null;
      host.replaceChildren();
    }
  }, [showIframe, youtube_id]);

  // Disconnect the observer on unmount.
  useEffect(() => () => observerRef.current?.disconnect(), []);

  const playVideo = () => {
    if (!useUserStore.getState().user) {
      useUserStore.getState().checkUserHasExceededAllowedSongs();
    }

    usePlayerStore.getState().setPlayingIframe(youtube_id);
    onPlaySongClicked();
    setShowIframe(true);
  };

  useImperativeHandle(ref, () => ({ playVideo }));

  return (
    <div className={className}>
      <div
          data-play-target
          onClick={playVideo}
          className={`youtube-container${showIframe ? ' !z-50' : ''}`}>
        {showIframe && !iframeLoaded && <SpinnerLoading />}
        <div ref={iframeHostRef} className="w-full h-full" />
      </div>
    </div>
  );
}
